// src/core/data-tables/theme.ts

/**
 * Pre-defined colour-scheme classes for DataTable.
 * Each key maps to Tailwind utility strings used by <DataTable/>.
 *
 * Only the “default” palette is redesigned here – existing colourScheme
 * props continue to work for all keys.
 */
export const themes: Record<string, { header: string; row: string; alt: string }> = {
  /* ────────────────────────────────────────────────────────────────
     Neutral / default palette – plain header text, gentle zebra rows
  ───────────────────────────────────────────────────────────────── */
  default: {
    header: [
      // background + text colour
      "bg-gray-50 dark:bg-gray-800",
      "text-gray-700 dark:text-gray-100",
      // subtle border + consistent font weight (no uppercase)
      "border-b border-gray-200 dark:border-gray-700",
      "font-medium text-sm",
    ].join(" "),
    row: "hover:bg-gray-50 dark:hover:bg-gray-800/50",
    // slightly darker zebra for better contrast
    alt: "bg-gray-100 dark:bg-gray-900/70",
  },

  /* ────────────────────────────────────────────────────────────────
     Blue theme – darker zebra striping, header text unchanged
  ───────────────────────────────────────────────────────────────── */
  blue: {
    header:
      "bg-blue-50 dark:bg-blue-950 text-blue-900 dark:text-blue-100 " +
      "border-b border-blue-200/60 dark:border-blue-800 font-medium text-sm",
    row: "hover:bg-blue-50/50 dark:hover:bg-blue-950/50",
    alt: "bg-blue-100/60 dark:bg-blue-950/60", // darker highlight
  },

  /* ────────────────────────────────────────────────────────────────
     Gray theme – darker zebra striping, header text unchanged
  ───────────────────────────────────────────────────────────────── */
  gray: {
    header:
      "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 " +
      "border-b border-gray-300/60 dark:border-gray-700 font-medium text-sm",
    row: "hover:bg-gray-50/70 dark:hover:bg-gray-800/70",
    alt: "bg-gray-200/60 dark:bg-gray-800/60",
  },

  /* ────────────────────────────────────────────────────────────────
     Emerald / green theme – darker zebra striping, header text unchanged
  ───────────────────────────────────────────────────────────────── */
  green: {
    header:
      "bg-emerald-50 dark:bg-emerald-950 text-emerald-900 dark:text-emerald-100 " +
      "border-b border-emerald-200/60 dark:border-emerald-800 font-medium text-sm",
    row: "hover:bg-emerald-50/50 dark:hover:bg-emerald-950/50",
    alt: "bg-emerald-100/60 dark:bg-emerald-950/60",
  },
};

/** Helper – returns the requested palette or falls back to “default”. */
export function getTheme(
  colorScheme: "default" | "blue" | "gray" | "green",
) {
  return themes[colorScheme] || themes.default;
}